/**
 * TopShelf Footer
 *  - Live "OPEN NOW · Closes 7:00 PM" status using customer local time
 *    Hours are read from data-store-hours JSON on the .topshelf-footer__status element.
 *    Format: { "mon": [11,19], "tue": [11,19], ..., "sun": [11,19] }  (24h, [open, close])
 *  - Back-to-top button.
 *  - Reef Command live-chat trigger for Gorgias.
 *  - Attentive (email + SMS) dual opt-in signup. The form posts form-urlencoded
 *    in `no-cors` mode so it works without ACAO headers on the backend; we show
 *    optimistic success once the request resolves and rely on client-side
 *    validation to catch most invalid inputs before sending.
 *  - Entrance trigger for the shared .tsm-reveal / .tsm-cascade classes in topshelf-motion.css. It lives here
 *    because this file loads on every page; the reviews section and others rely on it too.
 */
(function initTopshelfFooter() {
  if (window.__topshelfFooterBound) return;
  window.__topshelfFooterBound = true;

  function dayKey(d) {
    return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][d.getDay()];
  }

  function fmtTime(hour24, minute) {
    const d = new Date();
    d.setHours(hour24, minute || 0, 0, 0);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  function updateStatus(el) {
    let hours;
    try { hours = JSON.parse(el.dataset.storeHours || '{}'); } catch (e) { return; }
    const now = new Date();
    const today = hours[dayKey(now)];
    const labelEl = el.querySelector('.topshelf-footer__status-label');
    const subEl   = el.querySelector('.topshelf-footer__status-sub');
    if (!labelEl || !subEl) return;

    if (!today || today.length !== 2) {
      el.dataset.open = 'false';
      labelEl.textContent = 'Closed Today';
      subEl.textContent = '';
      return;
    }

    const [openH, closeH] = today;
    const minutes = now.getHours() * 60 + now.getMinutes();
    const openMin  = openH * 60;
    const closeMin = closeH * 60;

    if (minutes >= openMin && minutes < closeMin) {
      el.dataset.open = 'true';
      labelEl.textContent = 'Open Now';
      subEl.textContent = '· Closes ' + fmtTime(closeH);
    } else if (minutes < openMin) {
      el.dataset.open = 'false';
      labelEl.textContent = 'Closed';
      subEl.textContent = '· Opens ' + fmtTime(openH);
    } else {
      el.dataset.open = 'false';
      labelEl.textContent = 'Closed';
      // Look up tomorrow's open time
      const next = new Date(now); next.setDate(now.getDate() + 1);
      const tomorrow = hours[dayKey(next)];
      if (tomorrow && tomorrow.length === 2) {
        subEl.textContent = '· Opens ' + fmtTime(tomorrow[0]) + ' tomorrow';
      } else {
        subEl.textContent = '';
      }
    }
  }

  function initStatus() {
    document.querySelectorAll('.topshelf-footer__status[data-store-hours]').forEach(updateStatus);
  }

  /** Horizon scrolls .page-wrapper instead of the document at 990px and up. */
  function getScrollContainer() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && getComputedStyle(wrapper).overflowY === 'auto') return wrapper;
    return document.scrollingElement || document.documentElement;
  }

  function initBackToTop() {
    document.querySelectorAll('.topshelf-footer__top').forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', () => {
        getScrollContainer().scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  }

  function openGorgiasChat() {
    const chat = window.GorgiasChat;
    if (!chat) return false;

    if (typeof chat.open === 'function') {
      chat.open();
      return true;
    }

    if (typeof chat.openChat === 'function') {
      chat.openChat();
      return true;
    }

    return false;
  }

  function initGorgiasChatLinks() {
    document.querySelectorAll('[data-topshelf-gorgias-chat]').forEach(link => {
      if (link.dataset.bound) return;
      link.dataset.bound = '1';

      link.addEventListener('click', event => {
        event.preventDefault();

        if (openGorgiasChat()) return;
        if (typeof window.__topshelfLoadGorgias === 'function') {
          window.__topshelfLoadGorgias();
        }

        let attempts = 0;
        const fallbackHref = link.getAttribute('href');
        const retry = window.setInterval(() => {
          attempts += 1;

          if (openGorgiasChat()) {
            window.clearInterval(retry);
            return;
          }

          if (attempts >= 80) {
            window.clearInterval(retry);
            if (fallbackHref) window.location.href = fallbackHref;
          }
        }, 100);
      });
    });
  }

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function normalizePhoneUS(raw) {
    const digits = String(raw || '').replace(/\D+/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return null;
  }

  function setStatus(form, state, message) {
    const el = form.querySelector('.topshelf-footer__signup-status');
    if (!el) return;
    if (!state) {
      el.hidden = true;
      el.removeAttribute('data-state');
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.setAttribute('data-state', state);
    el.textContent = message;
  }

  function markInvalid(input, invalid) {
    if (!input) return;
    if (invalid) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  async function submitAttentive(form) {
    const endpoint = form.dataset.endpoint || form.getAttribute('action');
    if (!endpoint) return;

    const emailInput = form.querySelector('input[name="email"]');
    const phoneInput = form.querySelector('input[name="phone"]');
    const email = (emailInput && emailInput.value || '').trim();
    const phoneRaw = (phoneInput && phoneInput.value || '').trim();

    markInvalid(emailInput, false);
    markInvalid(phoneInput, false);

    if (!EMAIL_RE.test(email)) {
      markInvalid(emailInput, true);
      setStatus(form, 'error', 'Please enter a valid email address.');
      if (emailInput) emailInput.focus();
      return;
    }

    const phoneE164 = normalizePhoneUS(phoneRaw);
    if (!phoneE164) {
      markInvalid(phoneInput, true);
      setStatus(form, 'error', 'Please enter a valid 10-digit US phone number.');
      if (phoneInput) phoneInput.focus();
      return;
    }

    setStatus(form, null);
    form.classList.add('is-submitting');

    const body = new URLSearchParams();
    body.set('email', email);
    body.set('phone', phoneE164);

    try {
      await fetch(endpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: body.toString(),
        credentials: 'omit',
        keepalive: true,
      });

      form.classList.remove('is-submitting');
      form.classList.add('is-success');
      setStatus(form, 'success', "You're in. Watch your inbox and texts for reef drops & VIP perks.");
      if (emailInput) emailInput.value = '';
      if (phoneInput) phoneInput.value = '';
    } catch (err) {
      form.classList.remove('is-submitting');
      setStatus(form, 'error', "Something went wrong submitting your signup. Please try again in a moment.");
    }
  }

  function initAttentiveSignup() {
    document.querySelectorAll('form[data-topshelf-attentive]').forEach(form => {
      if (form.dataset.bound) return;
      form.dataset.bound = '1';
      form.addEventListener('submit', event => {
        event.preventDefault();
        submitAttentive(form);
      });
    });
  }

  // Collapsible footer menu columns on mobile. Columns expand normally on
  // larger screens; below the mobile breakpoint each column's heading acts as
  // an accordion toggle.
  const FOOTER_MOBILE_MQ = window.matchMedia('(max-width: 640px)');

  function applyAccordionState(col, head) {
    if (FOOTER_MOBILE_MQ.matches) {
      head.setAttribute('role', 'button');
      head.setAttribute('tabindex', '0');
      head.setAttribute('aria-expanded', col.classList.contains('is-open') ? 'true' : 'false');
    } else {
      head.removeAttribute('role');
      head.removeAttribute('tabindex');
      head.removeAttribute('aria-expanded');
      col.classList.remove('is-open');
    }
  }

  function initFooterAccordions() {
    const cols = document.querySelectorAll('.topshelf-footer__col');
    cols.forEach(col => {
      const head = col.querySelector('.topshelf-footer__col-head');
      const list = col.querySelector('ul');
      if (!head || !list) return;

      if (!col.dataset.accordionBound) {
        col.dataset.accordionBound = '1';

        head.addEventListener('click', () => {
          if (!FOOTER_MOBILE_MQ.matches) return;
          const open = !col.classList.contains('is-open');
          col.classList.toggle('is-open', open);
          head.setAttribute('aria-expanded', open ? 'true' : 'false');
        });

        head.addEventListener('keydown', event => {
          if (!FOOTER_MOBILE_MQ.matches) return;
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            head.click();
          }
        });
      }

      applyAccordionState(col, head);
    });

    if (!initFooterAccordions.bound) {
      initFooterAccordions.bound = true;
      const onChange = () => {
        document.querySelectorAll('.topshelf-footer__col').forEach(col => {
          const head = col.querySelector('.topshelf-footer__col-head');
          if (head) applyAccordionState(col, head);
        });
      };
      if (typeof FOOTER_MOBILE_MQ.addEventListener === 'function') {
        FOOTER_MOBILE_MQ.addEventListener('change', onChange);
      } else if (typeof FOOTER_MOBILE_MQ.addListener === 'function') {
        FOOTER_MOBILE_MQ.addListener(onChange);
      }
    }
  }

  // .tsm-reveal elements and .tsm-cascade lists get .is-in once they scroll into view, which
  // plays the topshelf-motion.css entrance. From 750px up, a .tsm-stagger parent is watched as
  // one row so its items play in their --tsm-i order; on phones each element is watched alone
  // and whatever enters together is ordered on the fly.
  // Each target's first observer report decides its fate: if it is on screen then, it is
  // marked .is-seen and never hidden. html.tsm-js (which hides the rest) is only added after
  // that first report, so nothing visible at load blinks out. Geometry is never read up front:
  // that would force layout of content-visibility: auto sections the browser is skipping.
  let revealObserver;
  const revealGroups = new WeakMap();
  const revealFirst = new WeakSet();
  const revealQueued = new WeakSet();

  function initReveals() {
    if (!('IntersectionObserver' in window)) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(entries => {
        let n = 0;
        entries.forEach(entry => {
          const target = entry.target;
          const first = revealFirst.has(target);
          revealFirst.delete(target);
          if (first ? !entry.isIntersecting : entry.intersectionRatio < 0.15) return;
          revealObserver.unobserve(target);
          const items = revealGroups.get(target) || [target];
          revealGroups.delete(target);
          if (first) {
            items.forEach(el => el.classList.add('is-seen'));
            return;
          }
          if (items[0] === target && !target.classList.contains('tsm-cascade')) {
            target.style.setProperty('--tsm-i', Math.min(n++, 6));
          }
          items.forEach(el => el.classList.add('is-in'));
        });
        document.documentElement.classList.add('tsm-js');
      }, { threshold: 0.2 });
    }

    const wide = window.matchMedia('(min-width: 750px)').matches;
    document.querySelectorAll(
      '.tsm-reveal:not(.is-in):not(.is-seen), .tsm-cascade:not(.is-in):not(.is-seen)'
    ).forEach(el => {
      if (revealQueued.has(el)) return;
      revealQueued.add(el);
      const row = wide && el.classList.contains('tsm-reveal') && el.closest('.tsm-stagger');
      if (row && revealGroups.has(row)) {
        revealGroups.get(row).push(el);
        return;
      }
      if (row) revealGroups.set(row, [el]);
      const target = row || el;
      revealFirst.add(target);
      revealObserver.observe(target);
    });
  }

  function init() {
    initStatus();
    initBackToTop();
    initGorgiasChatLinks();
    initAttentiveSignup();
    initFooterAccordions();
    initReveals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Refresh open/closed badge every minute so transitions happen live.
  setInterval(initStatus, 60_000);

  document.addEventListener('shopify:section:load', init);
})();
